import type { useNewMorphemeLessonRevealPressMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme._lib.reveal";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { tinykeys } from "@-/com.npmjs.tinykeys";
import useLatest from "@-/com.npmjs.use-latest";
import { Pressable, Platform, View } from "@-/com.npmjs.react-native";
import { useEffect, type ReactNode } from "react";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";

export function LessonPageContainer({
  children,
  headerEnd,
  isActive = true,
  onReplayPress,
  revealMutation,
}: {
  children: ReactNode;
  headerEnd?: ReactNode;
  isActive?: boolean;
  onReplayPress?: () => void;
  revealMutation: ReturnType<typeof useNewMorphemeLessonRevealPressMutation>;
}) {
  const isFocused = useIsFocused();
  const isFocusedRef = useLatest(isFocused && isActive);
  const isPendingRef = useLatest(revealMutation.isPending);
  const triggerRef = useLatest(() => {
    revealMutation.mutate();
  });

  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }
    return tinykeys(globalThis.window, {
      Enter: (event) => {
        if (!isFocusedRef.current || isPendingRef.current) {
          return;
        }
        event.preventDefault();
        triggerRef.current();
      },
    });
  }, [isFocusedRef, isPendingRef, triggerRef]);

  return (
    <View className="min-h-0 flex-1 flex-col bg-surface pt-10">
      <View className="min-h-0 flex-1 overflow-hidden">
        {/* Wrap the content so empty-space touches also reveal, as soon as the finger lands.
            Nested controls handle their own presses. Omit the button role to avoid nested button
            elements on web. */}
        <Pressable
          accessible={false}
          className="min-h-0 flex-1"
          disabled={!isActive || revealMutation.isPending}
          onPressIn={() => revealMutation.mutate()}
          testID="lesson-reveal-pressable"
        >
          {children}
        </Pressable>
      </View>
      {onReplayPress ? (
        <Pressable
          accessibilityLabel="Replay stroke animation"
          accessibilityRole="button"
          className="absolute top-2.25 left-2.25 size-6 items-center justify-center rounded-full active:bg-accent"
          disabled={!isActive || revealMutation.isPending}
          hitSlop={10}
          onPress={onReplayPress}
          testID="lesson-replay-strokes"
        >
          <Icon name="LuRotateCcw" size={24} />
        </Pressable>
      ) : null}
      <View className="absolute top-2.25 right-2.25" pointerEvents="box-none">
        {headerEnd}
      </View>
    </View>
  );
}
